import React, { useState } from 'react';

import { CircularProgress, Drawer, MenuItem } from '@mui/material';
import Divider from '@mui/material/Divider';
import { IconMenuItem } from 'mui-nested-menu';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useMutationDeleteReport } from 'api/mutations/deleteReport.mutation';
import { useGetReportComments } from 'api/queries/getReportComments';

import { ConfirmDeleteModal } from 'components/common/ConfirmDeleteModal/ConfirmDeleteModal';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import { ReportFavoriteIcon } from 'components/common/ReportFavoriteIcon/ReportFavoriteIcon';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';

import { CreateTemplateFromReportModal } from 'components/Dashboards/Reporting/CreateTemplateFromReportModal/CreateTemplateFromReportModal';
import { ReportSettingsModal } from 'components/Dashboards/Reporting/ReportSettingsModal/ReportSettingsModal';
import { ReportNameWithLink } from 'components/Dashboards/Reporting/reportNameWithLink/ReportNameWithLink';
import { Report } from 'components/Dashboards/Reporting/reporting.types';
import { Button } from 'components/DesignSystem/Button';
import { CustomizedDropdownMenu, CustomizedTooltip } from 'components/common';
import { ReportComments } from 'components/pages/ReportPage/ReportPageHeader/reportComments/ReportComments';
import classes from 'components/pages/ReportPage/components/ReportPageHeader/ReportPageHeader.module.scss';
import { useNarrativeWidgetsReady } from 'components/pages/ReportPage/components/ReportPageHeader/hooks/useNarrativeWidgetsReady';

interface Props {
    report: Report;
    onAddComponent: () => void;
    onFullscreenChange(): void;
    isFullscreen: boolean;
    onExportToPdf: () => void;
    onExportToGridPptx: () => void;
}
const MENU_FONT_SIZE = '13px';
const MENU_ICON_SIZE = '20px';

export const ReportPageHeader = ({
    report,
    onAddComponent,
    onFullscreenChange,
    isFullscreen,
    onExportToPdf,
    onExportToGridPptx,
}: Props) => {
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [isReportSettingsModalOpen, setIsReportSettingsModalOpen] = useState<boolean>(false);
    const [isTemplateFromReportModalOpen, setIsTemplateFromReportModalOpen] = useState<boolean>(false);
    const [displayCommentsDrawer, setDisplayCommentsDrawer] = useState<boolean>(false);

    // Narrative widgets readiness state for export buttons
    const { isAllNarrativeWidgetsReady } = useNarrativeWidgetsReady({ widgets: report.components });
    const isExportDisabled = !isAllNarrativeWidgetsReady;

    //queries
    const queryClient = useQueryClient();
    const { mutate: deleteReport, isLoading: isDeleteReportLoading } = useMutationDeleteReport();
    const { data: reportCommentsData } = useGetReportComments({
        reportId: report.id,
    });

    //hooks
    const history = useHistory();
    const { handleSnackBar } = useCustomSnackBar();
    const { sendEventWithDimensions } = useEventWithDimensions();

    //callbacks
    const onNavigateToReportingHomePage = (): void => {
        const url = AppRoutes.buildPath(AppRoutes.DASHBOARD_REPORTING_PATH);
        history.push(url);
    };

    const onDeleteReport = (reportId: string): void => {
        deleteReport(
            { reportId: reportId },
            {
                onSuccess: async () => {
                    sendEventWithDimensions({
                        category: 'Reporting',
                        action: 'Report Deleted',
                        label: 'None',
                    });
                    handleSnackBar('Report removed successfully', 'success');
                    setIsDeleteModalOpen(false);
                    await queryClient.invalidateQueries('getReportList');
                    onNavigateToReportingHomePage();
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            },
        );
    };

    const handleAddComponentClick = (): void => {
        sendEventWithDimensions({
            category: 'Reporting',
            action: 'Component Library Opened',
            label: 'none',
        });
        onAddComponent();
    };

    const handleExportToPptx = (): void => {
        sendEventWithDimensions({
            category: 'Reporting',
            action: 'Exported to PPT',
            label: 'None',
        });
        onExportToGridPptx();
    };

    const handleExportToPdf = (): void => {
        sendEventWithDimensions({
            category: 'Reporting',
            action: 'Exported to PDF',
            label: 'None',
        });
        onExportToPdf();
    };

    return (
        <div data-testid={'report-page-header'} className={classes.reportPageHeader}>
            <Drawer
                anchor={'right'}
                open={displayCommentsDrawer}
                onClose={() => setDisplayCommentsDrawer(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: 'var(--bg-primary)',
                    },
                }}
            >
                <div className={classes.commentsWrapper}>
                    <ReportComments reportId={report.id} setDisplayCommentsDrawer={setDisplayCommentsDrawer} />
                </div>
            </Drawer>
            <div className={classes.leftSection}>
                <ReportNameWithLink report={report} />
                <ReportFavoriteIcon id={report.id} isFavorite={report.isMyFavorite} showLoader={true} />
            </div>

            <div className={classes.rightSection}>
                <Button
                    variant={'tertiary'}
                    onClick={onFullscreenChange}
                    startIcon={<MuiIcon icon={'crop_free'} color={'inherit'} />}
                >
                    {isFullscreen ? 'Exit presentation mode' : 'Presentation mode'}
                </Button>

                {!isFullscreen && (
                    <>
                        <ShareInsightButton
                            title={'Share'}
                            link={window.location.href}
                            customTriggerElement={
                                <Button variant={'tertiary'} startIcon={<MuiIcon icon={'share'} color={'inherit'} />}>
                                    Share
                                </Button>
                            }
                        />

                        <Button
                            variant={'tertiary'}
                            onClick={() => setDisplayCommentsDrawer(true)}
                            startIcon={<MuiIcon icon={'comments'} color={'var(button-tertiary-fg)'} />}
                        >
                            <span className={classes.commentsButtonText}>Comments</span>
                            {!reportCommentsData ? (
                                <CircularProgress size={14} sx={{ color: '--var(button-tertiary-fg)' }} />
                            ) : (
                                <span>{`(${reportCommentsData?.length})`}</span>
                            )}
                        </Button>

                        <CustomizedDropdownMenu
                            menuStyles={{ maxHeight: 'initial' }}
                            triggerElement={
                                <CustomizedTooltip
                                    tooltipContent={`Manage Activity`}
                                    triggerElement={
                                        <Button variant={'tertiary'}>
                                            <MuiIcon fontSize={'25px'} icon={'more_vert'} color={'inherit'} />
                                        </Button>
                                    }
                                />
                            }
                        >
                            <MenuItem>
                                <IconMenuItem
                                    sx={{
                                        '& .MuiTypography-root': {
                                            fontSize: MENU_FONT_SIZE,
                                            position: 'relative',
                                        },
                                        '& .MuiBox-root': {
                                            alignItems: 'center',
                                        },
                                    }}
                                    leftIcon={
                                        <MuiIcon icon={'dashboard_customize_outlined'} fontSize={MENU_ICON_SIZE} />
                                    }
                                    onClick={() => {
                                        setIsTemplateFromReportModalOpen(true);
                                    }}
                                    label={'Use report as template'}
                                />
                            </MenuItem>
                            <MenuItem>
                                <IconMenuItem
                                    sx={{
                                        '& .MuiTypography-root': {
                                            fontSize: MENU_FONT_SIZE,
                                            position: 'relative',
                                        },
                                        '& .MuiBox-root': {
                                            alignItems: 'center',
                                        },
                                    }}
                                    leftIcon={<MuiIcon icon={'tune'} fontSize={MENU_ICON_SIZE} />}
                                    onClick={() => {
                                        setIsReportSettingsModalOpen(true);
                                    }}
                                    label={'Report settings'}
                                />
                            </MenuItem>
                            <Divider />
                            <MenuItem disabled={isExportDisabled}>
                                <IconMenuItem
                                    sx={{
                                        '& .MuiTypography-root': {
                                            fontSize: MENU_FONT_SIZE,
                                            position: 'relative',
                                            top: '1px',
                                        },
                                        '& .MuiBox-root': {
                                            alignItems: 'center',
                                        },
                                    }}
                                    leftIcon={<MuiIcon icon={'file_open_outlined'} fontSize={MENU_ICON_SIZE} />}
                                    onClick={isExportDisabled ? undefined : handleExportToPptx}
                                    label={isExportDisabled ? 'Export to PPTX (Loading...)' : 'Export to PPTX'}
                                />
                            </MenuItem>
                            <MenuItem disabled={isExportDisabled}>
                                <IconMenuItem
                                    sx={{
                                        '& .MuiTypography-root': {
                                            fontSize: MENU_FONT_SIZE,
                                            position: 'relative',
                                        },
                                        '& .MuiBox-root': {
                                            alignItems: 'center',
                                        },
                                    }}
                                    leftIcon={<MuiIcon icon={'picture_as_pdf'} fontSize={MENU_ICON_SIZE} />}
                                    onClick={isExportDisabled ? undefined : handleExportToPdf}
                                    label={isExportDisabled ? 'Export to PDF (Loading...)' : 'Export to PDF'}
                                />
                            </MenuItem>
                            <Divider />
                            <MenuItem>
                                <IconMenuItem
                                    sx={{
                                        '& .MuiTypography-root': {
                                            fontSize: MENU_FONT_SIZE,
                                            position: 'relative',
                                            color: `var(--menu-destructive-fg)`,
                                            top: '1px',
                                        },
                                        '& .MuiBox-root': {
                                            alignItems: 'center',
                                        },
                                    }}
                                    leftIcon={
                                        <MuiIcon
                                            icon={'delete'}
                                            type={'outlined'}
                                            fontSize={MENU_ICON_SIZE}
                                            color={`var(--menu-destructive-fg)`}
                                        />
                                    }
                                    onClick={() => {
                                        setIsDeleteModalOpen(true);
                                    }}
                                    label={'Delete report'}
                                />
                            </MenuItem>
                        </CustomizedDropdownMenu>
                        <Button
                            variant={'primary'}
                            onClick={handleAddComponentClick}
                            startIcon={<MuiIcon icon={'add'} color={'white'} />}
                        >
                            Add component
                        </Button>
                    </>
                )}
                <ConfirmDeleteModal
                    open={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    action={() => onDeleteReport(report.id.toString())}
                    title={'Delete Report'}
                    isLoading={isDeleteReportLoading}
                >
                    {`Do you want to delete '${report.name}' Report?`}
                </ConfirmDeleteModal>

                <ReportSettingsModal
                    isModalOpen={isReportSettingsModalOpen}
                    setIsModalOpen={setIsReportSettingsModalOpen}
                />
                <CreateTemplateFromReportModal
                    isModalOpen={isTemplateFromReportModalOpen}
                    setIsModalOpen={setIsTemplateFromReportModalOpen}
                />
            </div>
        </div>
    );
};
